/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, type ReactElement, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { useBasketSnapshot } from '@/providers/basket';
import { useMiniCartStore } from '@/hooks/mini-cart-store';
import { routes } from '@/route-paths';
import CartBadgeIcon from './cart-badge-icon';

const CartSheet = lazy(() => import('./cart-sheet'));

/**
 * Header cart button. It navigates straight to the cart page on desktop and mobile; it never opens
 * the mini cart itself.
 *
 * The mini cart sheet is still mounted here — lazily, and only once something opens it
 * programmatically (e.g. add to cart via `setMiniCartOpen(true)`). It stays mounted afterwards so
 * the close animation can play and later opens are instant.
 */
export default function CartBadge(): ReactElement {
    const snapshot = useBasketSnapshot();
    const { t } = useTranslation('cart');
    const numberOfItems = snapshot?.uniqueProductCount ?? 0;
    const miniCartOpen = useMiniCartStore((s) => s.open);
    const [sheetMounted, setSheetMounted] = useState<boolean>(false);

    if (miniCartOpen && !sheetMounted) {
        setSheetMounted(true);
    }

    return (
        <>
            <Button
                asChild
                variant="ghost"
                className="relative cursor-pointer size-9 p-2 has-[>svg]:px-2 lg:h-[34px] lg:w-auto lg:py-1.5 lg:has-[>svg]:px-3 hover:bg-transparent hover:opacity-50 transition-opacity">
                <Link to={routes.cart} aria-label={t('badge.ariaLabel', { count: numberOfItems })}>
                    <CartBadgeIcon numberOfItems={numberOfItems} />
                </Link>
            </Button>
            {sheetMounted && (
                <Suspense fallback={null}>
                    <CartSheet />
                </Suspense>
            )}
        </>
    );
}
